import { useState } from "react";
import { useNavigate, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { signIn, signUp } from "../lib/auth-client";
import { Button } from "@/components/ui/button";
import AuthLayout from "@/components/AuthLayout";
import AuthLangSwitcher from "@/components/AuthLangSwitcher";
import AuthThemeToggle from "@/components/AuthThemeToggle";
import { marketingUrl } from "@/lib/marketing-url";
import { validateName, NAME_MIN, NAME_MAX } from "@/lib/validate-name";

export default function SignUp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const nameResult = validateName(name);
    if (!nameResult.ok) {
      setError(t(`nameError.${nameResult.error}`));
      return;
    }
    const { error } = await signUp.email({
      name: nameResult.name,
      email,
      password,
    });
    if (error) {
      setError(error.message || "Sign up failed");
    } else {
      navigate({ to: "/activate" });
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    await signIn.social({
      provider: "google",
      callbackURL: window.location.origin + "/activate",
      errorCallbackURL: "/sign-up",
    });
  };

  return (
    <>
      <AuthLangSwitcher />
      <AuthThemeToggle />
      <AuthLayout>
      {/* Header */}
      <div className="flex flex-col items-center gap-3 text-center">
        <img src="/logo.png" alt="brinked.ai" className="h-12 w-12" />
        <h1 className="text-4xl font-heading font-semibold tracking-tight text-primary leading-none">
          brinked<span className="text-accent">.ai</span>
        </h1>
        <p className="text-lg text-muted-foreground">{t("signUpSubtitle")}</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="name"
            className="text-sm font-medium text-muted-foreground"
          >
            {t("name")}
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("namePlaceholder")}
            required
            minLength={NAME_MIN}
            maxLength={NAME_MAX}
            pattern="[A-Za-z][A-Za-z\s'\-]*[A-Za-z]"
            title={t("nameError.name_invalid_chars")}
            className="h-12 w-full border-0 border-b border-input bg-transparent px-0 text-foreground transition-all duration-200 placeholder:text-muted-foreground/60 focus:border-b-2 focus:border-primary focus:outline-none focus:ring-0"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="email"
            className="text-sm font-medium text-muted-foreground"
          >
            {t("email")}
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("emailPlaceholder")}
            required
            className="h-12 w-full border-0 border-b border-input bg-transparent px-0 text-foreground transition-all duration-200 placeholder:text-muted-foreground/60 focus:border-b-2 focus:border-primary focus:outline-none focus:ring-0"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="password"
            className="text-sm font-medium text-muted-foreground"
          >
            {t("password")}
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("passwordPlaceholder")}
            required
            className="h-12 w-full border-0 border-b border-input bg-transparent px-0 text-foreground transition-all duration-200 placeholder:text-muted-foreground/60 focus:border-b-2 focus:border-primary focus:outline-none focus:ring-0"
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button
          type="submit"
          className="mt-2 h-14 w-full text-sm uppercase tracking-wide"
        >
          {t("signUp")}
        </Button>
      </form>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">
            {t("or")}
          </span>
        </div>
      </div>

      {/* Google Sign In */}
      <Button
        type="button"
        variant="outline"
        className="h-14 w-full"
        onClick={handleGoogleSignIn}
      >
        <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        {t("continueWithGoogle")}
      </Button>

      {/* Footer */}
      <p className="text-center text-sm text-muted-foreground">
        {t("alreadyHaveAccount")}{" "}
        <Link
          to="/sign-in"
          className="font-medium text-primary transition-colors hover:text-primary/80"
        >
          {t("signIn")}
        </Link>
      </p>

      <p className="text-center text-xs text-muted-foreground">
        <a
          href={`${marketingUrl}/terms-of-service`}
          className="transition-colors hover:text-primary"
        >
          {t("termsOfService")}
        </a>
        <span className="mx-2">·</span>
        <a
          href={`${marketingUrl}/privacy-policy`}
          className="transition-colors hover:text-primary"
        >
          {t("privacyPolicy")}
        </a>
      </p>
    </AuthLayout>
    </>
  );
}
